import { ref, computed, watch, readonly } from 'vue'
import { createGlobalState } from '@vueuse/core'

import { useAuthStore } from '@/stores'
import { usePermissions } from './usePermissions'

export const useSidebarMenu = createGlobalState(
  () => {
    const { hasAccess, iteration } = usePermissions()
    const authStore = useAuthStore()
    const show = ref(true)

    const generateStudentMenu = computed(() => [
      {
        name: 'home',
        title: 'menu.home',
        to: '/home',
        icon: 'home',
        permission: 'Aprendizado'
      },
      {
        name: 'catalog',
        title: 'menu.catalog',
        to: '/catalog',
        icon: 'list-search',
        permission: 'Aprendizado'
      },
      // {
      //   name: 'journey',
      //   title: 'Minha Jornada',
      // },
      {
        name: 'progress',
        title: 'menu.myProgress',
        to: '/gestao/progresso',
        icon: 'progress-check',
        permission: 'Meu Progresso'
      },
      {
        name: 'my-competitions',
        title: 'menu.myGames',
        to: '/game/my-activity',
        icon: 'trophy',
        permission: 'Minhas Competições'
      },
      {
        name: 'myJourney',
        title: ['menu.myJourney', 2],
        to: '/learning-path/my-journey',
        icon: 'map',
        permission: 'Minhas Jornadas'
      },
      {
        name: 'myFavorites',
        title: 'menu.favorites',
        icon: 'bookmarks',
        to: '/catalog/favorites',
        permission: 'Aprendizado'
      },
      // {
      //   name: 'dashboard',
      //   title: 'menu.dashboard'
      // }
      {
        name: 'my-assessments',
        title: 'labels.myAssessments',
        icon: 'list-letters',
        to: '/gestao/minhas-avaliacoes',
        permission: 'Minhas Avaliações'
      },
      {
        name: 'my-panel',
        title: 'menu.myPanel',
        icon: 'graph',
        to: '/gestao/meu-painel',
        permission: 'Meu Painel'
      },
      {
        name: 'bi-dashboard',
        title: 'menu.dashboard',
        icon: 'chart-histogram',
        to: '/gestao/bi-dashboard',
        permission: 'Dashboard'
      },
      {
        name: 'bi-dashboard-report',
        title: 'menu.reports',
        icon: 'chart-histogram',
        to: '/gestao/report',
        permission: 'Dashboard'
      },
      {
        name: 'externalCertificates',
        title: 'menu.externalCertificates',
        icon: 'file-certificate',
        permission: 'Certificados Externos',
        to: '/gestao/certificados-externos'
      },
      {
        name: 'bi-management',
        title: 'menu.biManagement',
        to: '/gestao/bi-management',
        permission: 'Painéis Dashboard'
      }
    ].filter((el) => hasAccess(el.permission, 'Aprendizado') || hasAccess(el.permission, 'Gestão')))

    const generateCompleteMenu = computed(() => ([
      {
        name: 'learning',
        title: 'menu.learning',
        permission: '',
        icon: 'school',
        items: generateStudentMenu.value
      },
      {
        name: 'management',
        title: 'menu.management',
        permission: 'Gestão',
        icon: 'layout-dashboard',
        items: [
          {
            name: 'dashboard',
            title: "Dashboard v1",
            permission: 'Dashboard v1',
            to: '/gestao/dashboard'
          },
          {
            name: 'BI',
            title: 'menu.bi',
            permission: 'BI',
            to: 'https://bi.plantareducacao.com.br/',
            target: '_blank'
          },
          // {
          //   name: 'upload-history',
          //   title: 'Hist. envios',
          //   to: '/gestao/historico-envios'
          // },
          // {
          //   name: 'import-history',
          //   title: 'Hist. importações',
          //   to: '/gestao/importacoes'
          // }
          {
            name: 'corrections-assessments',
            title: 'labels.assessmentsCorrection',
            to: '/gestao/correcoes-avaliacoes',
            permission: 'Correções de Avaliações'
          },
        ].filter((el) => hasAccess(el.permission, 'Gestão'))
      },
      {
        name: 'account',
        title: 'menu.accounts',
        permission: 'Contas',
        icon: 'users',
        items: [
          // ...(
          //   authStore.inAdminMode ? [
          {
            name: 'companies',
            title: 'menu.companies',
            permission: 'Empresas',
            to: '/conta/pessoa',
            meta: { permission: 'plantar' }
          },
          {
            name: 'contracts',
            title: 'menu.contracts',
            permission: 'Contratos',
            to: '/conta/contrato',
            meta: { permission: 'plantar' }
          },
          {
            name: 'my-contract',
            title: 'menu.myContract',
            permission: 'Meu Contrato',
            to: '/conta/meu-contrato'
          },
          {
            name: 'units',
            title: 'menu.units',
            permission: 'Unidades de Negócio',
            to: '/conta/unidade'
          },
          {
            name: 'departments',
            title: 'menu.departments',
            permission: 'Departamentos',
            to: '/conta/departamento'
          },
          {
            name: 'roles',
            title: 'menu.jobsTitles',
            permission: 'Cargos',
            to: '/conta/cargo'
          },
          {
            name: 'users',
            title: 'menu.users',
            permission: 'Usuários',
            to: '/conta/usuario'
          }
        ]?.filter((el) => hasAccess(el.permission, 'Contas'))
      },
      {
        name: 'content',
        title: 'menu.content',
        permission: 'Conteúdo',
        icon: 'list-details',
        items: [
          {
            name: 'depot',
            title: 'menu.repository',
            permission: 'Depósito',
            to: '/content/files'
          },
          {
            name: 'tests',
            title: 'menu.tests',
            permission: 'Avaliações',
            to: '/conteudo/avaliacao'
          },
          {
            name: 'courses',
            title: 'menu.courses',
            permission: 'Cursos',
            to: '/conteudo/curso'
          },
          {
            name: 'formations',
            title: 'menu.trails',
            permission: 'Trilhas',
            to: '/conteudo/trilha'
          },
          {
            name: 'sections',
            title: 'menu.sections',
            permission: 'Seções',
            to: '/conteudo/secao'
          },
          {
            name: 'journey',
            title: ['menu.learningJourney', 2],
            to: '/learning-path/journey',
            permission: 'Jornadas de Aprendizado'
          },
          {
            name: 'manage-externalCertificates',
            title: 'menu.manageExternalCertificates',
            permission: 'Gerir Certificados Externos',
            to: '/conteudo/certificados-externos-admin'
          }
        ].filter((el) => hasAccess(el.permission, 'Conteúdo'))
      },
      {
        name: 'communications',
        title: 'menu.communications',
        permission: 'Comunicações',
        icon: 'message-dots',
        items: [
          {
            name: 'communication',
            title: 'menu.communication',
            permission: 'Comunicação',
            to: '/comunicacoes/comunicacao'
          },
          {
            name: 'automated-notifications',
            title: 'menu.automatedNotifications',
            permission: 'Notificações Automatizadas',
            to: '/comunicacoes/notificacoes-automatizadas'
          },
          {
            name: 'well-being-indicator',
            title: 'menu.wellBeingIndicator',
            permission: 'Indicador de Bem Estar',
            to: '/comunicacoes/indicador-de-bem-estar'
          }
        ].filter((el) => hasAccess(el.permission, 'Comunicações'))
      },
      {
        name: 'gamification',
        title: 'menu.gamification',
        permission: 'Gamificação',
        icon: 'award',
        items: [
          // {
          //   name: 'journey',
          //   title: 'menu.journey',
          //   permission: 'Jornada',
          //   to: '/gamificacao/jornada'
          // },
          {
            name: 'challenges',
            title: 'menu.challenges',
            permission: 'Desafios',
            to: '/gamificacao/desafios'
          },
          {
            name: 'competitions',
            title: 'menu.competitions',
            permission: 'Competições',
            to: '/game/admin'
          }
        ].filter((el) => hasAccess(el.permission, 'Gamificação'))
      },
      {
        name: 'settings',
        title: 'menu.settings',
        permission: 'Configurações',
        icon: 'adjustments-horizontal',
        items: [
          // {
          //   name: 'general-parameters',
          //   title: 'Parâmetros Gerais',
          //   to: '/settings/contrato',
          //   meta: { permission: 'plantar' }
          // },
          // {
          //   name: 'contracts',
          //   title: 'Contratos',
          //   to: '/settings/contrato',
          //   meta: { permission: 'plantar' }
          // },
          {
            name: 'customization',
            title: 'menu.customizations',
            permission: 'Personalizações',
            to: '/settings/personalizacoes'
          },
          {
            name: 'competences',
            title: 'menu.skills',
            permission: 'Competências',
            to: '/settings/competencia'
          },
          {
            name: 'permission-groups',
            title: 'menu.permissions',
            permission: 'Permissões',
            to: '/settings/permissoes'
          },
          {
            name: 'categories',
            title: 'menu.categories',
            permission: 'Categorias',
            to: '/settings/categoria'
          },
          {
            name: 'certificates',
            title: 'menu.certificates',
            permission: 'Certificados',
            to: '/settings/certificado'
          },
          {
            name: 'terms-of-use',
            title: 'menu.termsOfUse',
            permission: 'Termos de Uso',
            to: '/settings/termos-uso'
          },
          {
            name: 'segmentations',
            title: 'menu.segmentations',
            permission: 'Segmentações',
            to: '/settings/segmentacao'
          },
          {
            name: 'email-provider',
            title: 'menu.smtpConfiguration',
            permission: 'Provedor de Email',
            to: '/settings/provedor-de-email'
          }
        ].filter((el) => hasAccess(el.permission, 'Configurações'))
      }
    ].filter((el) => typeof el.items === 'undefined' || el.items.length > 0).filter((el) => hasAccess(el.permission))))

    const sideBarMenu = ref([])
    const updateMenu = () => {
      const m = generateCompleteMenu.value
      if (m.length > 1) {
        sideBarMenu.value = m
      } else {
        sideBarMenu.value = generateStudentMenu.value
      }
    }

    watch(
      () => authStore.inAdminMode,
      () => updateMenu(),
      { immediate: true }
    )

    watch(
      iteration,
      () => updateMenu()
    )

    return {
      sideBarMenu: computed(() => sideBarMenu.value),
      updateMenu,
      visible: readonly(show),
      showSidebar: () => show.value = true,
      hideSidebar: () => show.value = false
    }
  }
)